/**
 * Tiny encapsulation of the "Recent search queries" cache so SearchCommand
 * doesn't carry localStorage plumbing inline. Stores the last 8 queries
 * + their target paths/titles. Non-essential — silently no-ops on
 * localStorage failure (incognito / disabled storage).
 */
import { useCallback, useState } from "react";

import { log } from "@/lib/log";
const RECENT_KEY = "rp:search:recent";
const RECENT_MAX = 8;

function loadRecent() {
  try {
    const raw = localStorage.getItem(RECENT_KEY);
    if (!raw) return [];
    const arr = JSON.parse(raw);
    return Array.isArray(arr) ? arr.slice(0, RECENT_MAX) : [];
  } catch {
    return [];
  }
}

export default function useRecentSearches() {
  const [recent, setRecent] = useState(() => loadRecent());

  const push = useCallback((q, item) => {
    try {
      const arr = loadRecent().filter((r) => r.q !== q);
      arr.unshift({
        q,
        path: item?.path || null,
        title: item?.title || q,
        ts: Date.now(),
      });
      localStorage.setItem(RECENT_KEY, JSON.stringify(arr.slice(0, RECENT_MAX)));
      setRecent(loadRecent());
    } catch (err) {
      // localStorage full or disabled — list is non-essential
      log.warn("[useRecentSearches] push failed:", err?.message);
    }
  }, []);

  const clear = useCallback(() => {
    try {
      localStorage.removeItem(RECENT_KEY);
    } catch (err) {
      log.warn("[useRecentSearches] clear failed:", err?.message);
    }
    setRecent([]);
  }, []);

  return { recent, push, clear };
}
